import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type InvoiceParams = {
  id: string;
};

const InvoiceDownload = () => {
  const { id } = useParams<InvoiceParams>();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("Fetching your invoice...");
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchInvoice = async () => {
      try {
        const baseUrl = process.env.REACT_APP_URL;
        const url = `${baseUrl}/invoice/pdf/${id}`;

        const res = await fetch(url);

        if (!res.ok) {
          const errorData = await res.json().catch(() => null);
          const message =
            errorData?.errors?.[0]?.title || "Failed to fetch invoice";
          throw new Error(message);
        }

        const data = await res.json();
        const pdfUrl = data?.data?.fileUrl;

        if (!pdfUrl) throw new Error("No file URL received from server");

        setFileUrl(pdfUrl);
        setStatus("success");
      } catch (error: any) {
        console.error("Invoice open error:", error.message);
        setStatus("error");
        setMessage(error.message || "Something went wrong while loading invoice.");
      }
    };

    fetchInvoice();
  }, [id]);

  // Loading state
  if (status === "loading") {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9fafb",
          color: "#333",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <h2>Opening Invoice #{id}</h2>
        <p>{message}</p>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#fef2f2",
          color: "#b91c1c",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <h2>Error Loading Invoice</h2>
        <p>{message}</p>
      </div>
    );
  }

  // Success state — show only the PDF
  return (
    <iframe
      src={fileUrl || ""}
      title={`Invoice #${id}`}
      style={{
        height: "100vh",
        width: "100%",
        border: "none",
      }}
    />
  );
};

export default InvoiceDownload;
